import React, { useEffect, useRef, useState } from 'react'

import isLoudVolume, { isLoudVolumeButAcceptable } from '../functions/isLoudVolume'
import isSoftVolume from '../functions/isSoftVolume'
import useStudentPreChecks from '../../useStudentPreChecks'
import { PRE_CHECK_MACHINE_STATES } from '../../constants'
import { MAX_COUNT_TO_EVALUATE_BEFORE_CALLING_TEACHER, MS_TO_WAIT_BETWEEN_EVALUATE_FOR_VOLUME_PROBLEM, VOLUME_CHECK_EVENTS } from '../StudentVolumeChecksConstants'

import { COPY_STRINGS } from '../../../../constants/translatedCopy'
import isAcceptableVolume from '../functions/isAcceptableVolume'

export default function VolumeEvaluationTimer(props) {

    const { speechProps, volumeStats, studentSpeechVolumeEvaluationCount, isTeacherMode, setShowTeacherTroubleshootScreen, onAcceptableVolumeCheckPassed } = props

    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks });
    const [volumeMeasurementDone, setVolumeMeasurementDone] = useState(false)

    const waitToEvaluateVolumeProblemsTimer = useRef()

    useEffect(() => {
        startWaitToEvaluateVolumeProblems()

        return () => {
            cancelWaitToEvaluateVolumeProblems()
        }
    }, [])

    useEffect(() => {
        if(volumeMeasurementDone){
            evaluateVolume()
        }
    }, [volumeMeasurementDone])

    const startWaitToEvaluateVolumeProblems = () => {
        setVolumeMeasurementDone(false)
        cancelWaitToEvaluateVolumeProblems()
        
        if(studentSpeechVolumeEvaluationCount.current < MAX_COUNT_TO_EVALUATE_BEFORE_CALLING_TEACHER){
            waitToEvaluateVolumeProblemsTimer.current = setTimeout(()=>{
                setVolumeMeasurementDone(true)
            }, MS_TO_WAIT_BETWEEN_EVALUATE_FOR_VOLUME_PROBLEM)
        }
    }
    const cancelWaitToEvaluateVolumeProblems = () => {
        console.log("cancel eval timer")
        clearTimeout(waitToEvaluateVolumeProblemsTimer.current)
    }
    const evaluateVolume = async () => {
        if(isAcceptableVolume(volumeStats)){
            await handleAcceptableVolume(volumeStats)
        }else if(isSoftVolume(volumeStats)) {
            await handleTooSoft()
        }else if(isLoudVolume(volumeStats)) {
            await handleTooLoud()
        }else{
            await handleOtherVolumeConditions()
        }
        studentSpeechVolumeEvaluationCount.current = studentSpeechVolumeEvaluationCount.current + 1
    }

    const handleOtherVolumeConditions = async () => {
        studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_NEITHER_SOFT_NOR_LOUD : VOLUME_CHECK_EVENTS.NEITHER_SOFT_NOR_LOUD)
        // force pass as fallback because it's not too loud NOR too soft, it must be in between.
        // we will use the logged volume stats to determine how it ended up here.
        onAcceptableVolumeCheckPassed()
        studentPreCheck.addTroubleshootData({
            ready_unhandledVolume: {
                volumeSnapshot: volumeStats,
                trialCount: studentSpeechVolumeEvaluationCount.current
            }
        })
    }

    const handleAcceptableVolume = async () => {
        // still falls within the acceptable volume, but is it on the high side
        if(isLoudVolumeButAcceptable(volumeStats)){
            console.log("Volume handling good volume but a bit loud", volumeStats)
            studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_TEST_ALMOST_TOO_LOUD : VOLUME_CHECK_EVENTS.ALMOST_TOO_LOUD)
            onAcceptableVolumeCheckPassed({loudButAcceptable: true})
        }else{
            console.log("Volume handling good volume", volumeStats)
            onAcceptableVolumeCheckPassed()
        }
    }

    const handleTooSoft = async () => {
        studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_TEST_TOO_SOFT : VOLUME_CHECK_EVENTS.TOO_SOFT)

        if(isTeacherMode){
            await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.teacher_mode_volume_failure_one })
            setShowTeacherTroubleshootScreen(true)
        }else{
            console.log("Volume handling soft speaking", volumeStats)
            studentPreCheck.addTroubleshootData({
                ready_handle_too_soft: {
                    volumeSnapshot: volumeStats,
                    trialCount: studentSpeechVolumeEvaluationCount.current
                }
            })
            if(studentSpeechVolumeEvaluationCount.current === 0){
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_soft_one })
                startWaitToEvaluateVolumeProblems()
            }else if(studentSpeechVolumeEvaluationCount.current === 1){
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_soft_second_round })
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_soft_second_round_two })
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_soft_second_round_three })
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_soft_second_round_four })
                startWaitToEvaluateVolumeProblems()
            } else if(studentSpeechVolumeEvaluationCount.current === 2){
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_get_teacher })
                setShowTeacherTroubleshootScreen(true)
            }
        }
    }

    const handleTooLoud = async () => {
        studentPreCheck.logEvent(isTeacherMode ? VOLUME_CHECK_EVENTS.TEACHER_TEST_TOO_LOUD : VOLUME_CHECK_EVENTS.TOO_LOUD)

        if(isTeacherMode){
            await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.teacher_mode_volume_failure_one })
            setShowTeacherTroubleshootScreen(true)
        }else{
            console.log("Volume handling loud speaking", volumeStats)
            studentPreCheck.addTroubleshootData({
                ready_handle_too_loud: {
                    volumeSnapshot: volumeStats,
                    trialCount: studentSpeechVolumeEvaluationCount.current
                }
            })
            if(studentSpeechVolumeEvaluationCount.current < 2){
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_loud_one })
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_loud_two })
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_too_loud_three })
                startWaitToEvaluateVolumeProblems()
            }else if(studentSpeechVolumeEvaluationCount.current === 2){
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.handle_get_teacher })
                setShowTeacherTroubleshootScreen(true)
            }
        }
    }

    return null
}
